/**
 * Microsoft Clarity, scoped to the pages where there is nothing to leak.
 *
 * Clarity is a session recorder, not a counter. It reports the real URL and
 * it reconstructs the DOM — so the two rules `analytics.ts` already follows
 * are not enough here, and one of them it cannot follow at all:
 *
 *   - **The URL is a credential on some routes.** `/sign/:token`,
 *     `/embed/:token` and `/completed/:token` are bearer URLs: whoever holds
 *     one can open the agreement. Clarity has no "report a pattern instead"
 *     switch the way GA4 does, so the only safe answer is never to let it
 *     run on those routes.
 *
 *   - **The page body is the customer's document.** `/prepare` renders an
 *     uploaded PDF, `/verify` renders one somebody dropped in, and `/app/*`
 *     renders counterparty names, email addresses and agreement contents. A
 *     recorder on any of those ships a third party the thing the product
 *     exists to protect.
 *
 * So this is an allow-list of *public marketing pages*, not the allow-list in
 * `analytics.ts` — that one covers everywhere a page view may be counted,
 * which is a much weaker bar than everywhere a screen may be recorded.
 *
 * The tag is also stopped on navigation. In a single-page app the script
 * outlives the route that loaded it: landing on `/pricing` and then opening
 * the dashboard would otherwise record the dashboard. `clarity('stop')` on
 * the way out is what keeps the allow-list meaning what it says.
 *
 * What that last part does and does not guarantee, stated plainly:
 *
 *   - A token route is protected *structurally*. A signing, embed or
 *     completed-copy link is opened from an email as a fresh document, and on
 *     a fresh document outside the allow-list the tag is never injected at
 *     all. There is no script to misbehave.
 *
 *   - `/prepare` and `/app/*` reached by a client-side navigation from a
 *     marketing page are protected *behaviourally*, by `clarity('stop')`. If
 *     the vendor script ever ignored its own documented call, that transition
 *     would be recorded. This is the same kind of trust `analytics.ts` places
 *     in GA4's `send_page_view: false`, and it is worth naming rather than
 *     leaving implicit.
 */

/** Public marketing routes. Everything else is excluded by omission, which
 *  is the direction an allow-list has to fail in. */
const RECORDABLE: RegExp[] = [
  /^\/$/,
  /^\/pricing\/?$/,
  /^\/blog\/?$/,
  /^\/blog\/[^/]+\/?$/,
  /^\/terms\/?$/,
  /^\/privacy\/?$/,
  /^\/support\/?$/,
  /^\/contact\/?$/,
  /^\/login\/?$/,
  /^\/register\/?$/,
];

/** True only for a page that carries no customer content and no token.
 *
 *  Deliberately excluded even though they are "public": `/prepare` (shows an
 *  uploaded document), `/verify` (shows a dropped-in document) and every
 *  `/app` and token route. */
export function recordable(pathname: string): boolean {
  return RECORDABLE.some((pattern) => pattern.test(pathname));
}

type Clarity = ((...args: unknown[]) => void) & { q?: unknown[] };

declare global {
  interface Window { clarity?: Clarity }
}

const PROJECT = 'z31rpo7pex';
const TAG_ID = 'ms-clarity';

let injected = false;
let running = false;

function inject(): void {
  if (injected || typeof document === 'undefined') return;
  injected = true;
  const w = window as Window & { clarity?: Clarity };
  w.clarity = w.clarity || (((...args: unknown[]) => {
    (w.clarity!.q = w.clarity!.q || []).push(args);
  }) as Clarity);
  const tag = document.createElement('script');
  tag.id = TAG_ID;
  tag.async = true;
  tag.src = `https://www.clarity.ms/tag/${PROJECT}`;
  const first = document.getElementsByTagName('script')[0];
  first?.parentNode?.insertBefore(tag, first);
}

/**
 * Call on every navigation.
 *
 * Loads the tag the first time a recordable page is shown, and starts or
 * stops recording as the route moves in and out of the allow-list. Stopping
 * rather than unloading is deliberate: the script cannot be un-executed, so
 * the thing that has to be reliable is that it is not capturing.
 */
export function syncClarity(pathname: string): void {
  if (typeof window === 'undefined') return;
  const allowed = recordable(pathname);

  if (allowed) {
    inject();
    if (!running) {
      window.clarity?.('start');
      running = true;
    }
    return;
  }

  if (running) {
    window.clarity?.('stop');
    running = false;
  }
}
